/**
 * Project persistence on IndexedDB (PRD §4.9).
 *
 * `localStorage` is deliberately not used for project data: its ~5 MB quota is
 * shared across the whole origin, it stores strings only, and every access is
 * synchronous on the main thread — a combination that fails exactly when a
 * project grows interesting. It remains fine for UI preferences, which is all
 * `prefs.ts` puts there.
 */

const DB_NAME = 'tola';
const DB_VERSION = 1;
const STORE = 'projects';

import type { Project } from '../engine/model.ts';

export interface ProjectRecord {
  id: string;
  name: string;
  project: Project;
  createdAt: number;
  updatedAt: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('updatedAt', 'updatedAt');
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Could not open the project database'));
    req.onblocked = () =>
      reject(new Error('Another tola tab is holding the database open — close it and retry'));
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const req = fn(transaction.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('Database request failed'));
      }),
  );
}

export function newProjectId(): string {
  return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export async function saveProject(record: ProjectRecord): Promise<void> {
  await tx('readwrite', (s) => s.put(record) as IDBRequest<IDBValidKey>);
}

export async function loadProject(id: string): Promise<ProjectRecord | undefined> {
  return tx('readonly', (s) => s.get(id) as IDBRequest<ProjectRecord | undefined>);
}

export async function listProjects(): Promise<ProjectRecord[]> {
  const all = await tx('readonly', (s) => s.getAll() as IDBRequest<ProjectRecord[]>);
  return all.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function deleteProject(id: string): Promise<void> {
  await tx('readwrite', (s) => s.delete(id) as unknown as IDBRequest<undefined>);
}

/** True when IndexedDB is unavailable (private browsing, ancient browser). */
export function storageAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined';
  } catch {
    return false;
  }
}
